import { VersionedTransaction } from '@solana/web3.js';
import { ApiKeyStamper, SignatureFormat } from '@turnkey/api-key-stamper';
import { generateP256KeyPair } from '@turnkey/crypto';
import { createActivityPoller, TurnkeyClient } from '@turnkey/http';
import { getClientSignatureMessageForLogin, OtpType } from '@turnkey/react-wallet-kit';
import { decodeJwtPayload } from '../../../../auth/oauth/oauth-claims.js';
import { mergeCreationSignatures } from './admin-creation-signatures.js';
import type { SdkTurnkeyContext } from './use-sdk-turnkey.js';

export interface CreatorSigningTarget {
  readonly organizationId: string;
  readonly walletId: string;
  readonly walletAddress: string;
}

export interface CreationWalletProof {
  turnkeySessionJwt: string;
  payerAddress: string;
  user: { body: string; stamp: string };
  wallet: { body: string; stamp: string };
}

/** Separate, memory-only operator session. Never activates a creator as the admin's Morfi user. */
export function createCreatorSigningSession(
  turnkey: Pick<SdkTurnkeyContext, 'initOtp' | 'verifyOtp' | 'httpClient'>,
  target: CreatorSigningTarget,
) {
  const selected = Object.freeze({ ...target });
  const pair = generateP256KeyPair();
  const stamper = new ApiKeyStamper({
    apiPublicKey: pair.publicKey,
    apiPrivateKey: pair.privateKey,
  });
  const client = new TurnkeyClient({ baseUrl: 'https://api.turnkey.com' }, stamper);
  let token: string | null = null;
  let userId: string | null = null;
  let expiresAt = 0;
  let disposed = false;
  let loginAttempt = 0;

  return Object.freeze({
    target: selected,
    async initOtp() {
      if (disposed) throw new Error('Creator session has closed. Select the creator again.');
      return turnkey.initOtp({ contact: 'markets@morfi.markets', otpType: OtpType.Email });
    },
    async verifyOtp(input: {
      otpId: string;
      otpEncryptionTargetBundle: string;
      otpCode: string;
    }): Promise<void> {
      if (disposed || !turnkey.httpClient) throw new Error('Creator sign-in is unavailable.');
      const attempt = ++loginAttempt;
      token = null;
      userId = null;
      const verification = await turnkey.verifyOtp({ ...input, publicKey: pair.publicKey });
      requireLoginAttempt(attempt);
      const { message } = getClientSignatureMessageForLogin({
        verificationToken: verification.verificationToken,
      });
      // Turnkey verifies the login client signature as raw r||s bytes, not DER
      // (the default `signWithApiKey` output), which it rejects as "invalid signature format".
      const signature = await stamper.sign(message, SignatureFormat.Raw);
      requireLoginAttempt(attempt);
      // Explicit organization selection is essential: this email belongs to multiple sub-orgs.
      // Calling completeOtp/loginWithOtp would also store/activate that identity in the paying wallet's kit.
      const result = await turnkey.httpClient.proxyOtpLoginV2({
        verificationToken: verification.verificationToken,
        publicKey: pair.publicKey,
        organizationId: selected.organizationId,
        invalidateExisting: false,
        clientSignature: {
          message,
          publicKey: pair.publicKey,
          signature,
          scheme: 'CLIENT_SIGNATURE_SCHEME_API_P256',
        },
      });
      requireLoginAttempt(attempt);
      const claims = decodeJwtPayload(result.session);
      if (
        claims === null ||
        claims.organization_id !== selected.organizationId ||
        typeof claims.user_id !== 'string' ||
        claims.user_id.length === 0 ||
        typeof claims.exp !== 'number' ||
        !Number.isFinite(claims.exp) ||
        claims.exp * 1000 <= Date.now()
      )
        throw new Error('The signing session does not match the selected creator.');
      // These claims select the request target; the backend verifies the JWT and live stamped reads.
      token = result.session;
      userId = claims.user_id;
      expiresAt = claims.exp * 1000;
    },
    async proof(): Promise<CreationWalletProof> {
      const session = requireSession();
      const [user, wallet] = await Promise.all([
        client.stampGetUser({ organizationId: selected.organizationId, userId: session.userId }),
        client.stampGetWalletAccounts({
          organizationId: selected.organizationId,
          walletId: selected.walletId,
        }),
      ]);
      requireSession();
      return {
        turnkeySessionJwt: session.token,
        payerAddress: selected.walletAddress,
        user: { body: user.body, stamp: user.stamp.stampHeaderValue },
        wallet: { body: wallet.body, stamp: wallet.stamp.stampHeaderValue },
      };
    },
    async signTransaction(transaction: Uint8Array): Promise<Uint8Array> {
      requireSession();
      const parsed = VersionedTransaction.deserialize(transaction);
      if (
        parsed.version !== 0 ||
        !parsed.message.staticAccountKeys
          .slice(0, parsed.message.header.numRequiredSignatures)
          .some((key) => key.toBase58() === selected.walletAddress)
      )
        throw new Error('The transaction does not require the selected creator.');
      const activity = await createActivityPoller({ client, requestFn: client.signTransaction })({
        type: 'ACTIVITY_TYPE_SIGN_TRANSACTION_V2',
        timestampMs: String(Date.now()),
        organizationId: selected.organizationId,
        parameters: {
          signWith: selected.walletAddress,
          unsignedTransaction: Buffer.from(transaction).toString('hex'),
          type: 'TRANSACTION_TYPE_SOLANA',
        },
      });
      requireSession();
      const signed = activity.result.signTransactionResult?.signedTransaction;
      if (!signed || !/^(?:[0-9a-f]{2})+$/i.test(signed))
        throw new Error('Turnkey did not return the creator signature.');
      const result = mergeCreationSignatures(parsed, new Uint8Array(Buffer.from(signed, 'hex')));
      const index = result.message.staticAccountKeys.findIndex(
        (key) => key.toBase58() === selected.walletAddress,
      );
      if (!result.signatures[index]?.some((byte) => byte !== 0))
        throw new Error('Turnkey did not return the creator signature.');
      return result.serialize();
    },
    dispose(): void {
      disposed = true;
      token = null;
      userId = null;
      pair.privateKey = '';
      stamper.apiPrivateKey = '';
    },
  });

  function requireLoginAttempt(attempt: number): void {
    if (disposed || attempt !== loginAttempt)
      throw new Error('Creator sign-in was replaced or closed. Select the creator again.');
  }

  function requireSession(): { token: string; userId: string } {
    if (disposed || token === null || userId === null || Date.now() >= expiresAt)
      throw new Error('Sign in again to authorize the selected creator.');
    return { token, userId };
  }
}

export type CreatorSigningSession = ReturnType<typeof createCreatorSigningSession>;
